// The integration's metadata key/value pairs, and their conversion into the
// object the API expects.

import { useCallback, useMemo, useRef, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import { t } from '@lingui/macro';

const entriesOf = (metadata) =>
  isEmpty(metadata) ? [] : cloneDeep(Object.entries(metadata));

const allFilled = (rows) =>
  isEmpty(rows) ||
  rows.every(
    (row) => !isEmpty(trim(row.key || '')) && !isEmpty(trim(row.value || '')),
  );

export default function useMetadataEditor({
  integration,
  setDisableSubmit,
  setError,
}) {
  // Rows carry a synthetic id purely so React can key them. Both columns are
  // edited in place, so neither is stable enough to key on, and keying by index
  // makes React hand a removed row's DOM node to its successor. The id never
  // leaves this hook — toObject rebuilds the wire object from the columns.
  const nextRowId = useRef(0);
  const mintId = useCallback(() => {
    nextRowId.current += 1;
    return `metadata-row-${nextRowId.current}`;
  }, []);
  const rowsOf = useCallback(
    (metadata) =>
      entriesOf(metadata).map(([key, value]) => ({
        rowId: mintId(),
        key,
        value,
      })),
    [mintId],
  );

  const [metadata, setMetadata] = useState(() =>
    rowsOf(integration.attributes.metadata),
  );

  const reportBlank = useCallback(
    (name, value) => {
      const blank = isEmpty(trim(value));
      setError(name, blank ? t`Can't be blank` : '');
      setDisableSubmit(blank);
    },
    [setError, setDisableSubmit],
  );

  const handlers = useMemo(
    () => ({
      addEntry: () => {
        setMetadata((previous) =>
          previous.concat([{ rowId: mintId(), key: '', value: '' }]),
        );
        setDisableSubmit(true);
      },
      changeKey: ({ e, item, index }) => {
        reportBlank(e.target.name, e.target.value);

        setMetadata((previous) => {
          const next = [...previous];
          next[index] = { ...item, key: e.target.value };
          setDisableSubmit(!allFilled(next));
          return next;
        });
      },
      changeValue: ({ e, item, index }) => {
        reportBlank(e.target.name, e.target.value);

        setMetadata((previous) => {
          const next = [...previous];
          next[index] = { ...item, value: e.target.value };
          setDisableSubmit(!allFilled(next));
          return next;
        });
      },
      removeEntry: (index) => {
        setMetadata((previous) => {
          const next = [...previous];
          next.splice(index, 1);
          setDisableSubmit(!allFilled(next));
          return next;
        });
      },
    }),
    [reportBlank, setDisableSubmit, mintId],
  );

  const reset = useCallback(
    (data) => {
      setMetadata(rowsOf(data.attributes.metadata));
    },
    [rowsOf],
  );

  // Blank keys are dropped rather than sent as an empty property.
  const toObject = useCallback(() => {
    const object = Object.fromEntries(
      metadata.map((row) => [row.key, row.value]),
    );
    Object.keys(object).forEach((key) => isEmpty(key) && delete object[key]);
    return object;
  }, [metadata]);

  return { metadata, handlers, reset, toObject };
}
